/**
 * Compiles the decomposed rhyme/structure config (RhymeConfig, StructureConfig
 * — src/types.ts) into the plain-English instruction text RiffWriter's prompt
 * builder (src/services/llmShared.ts) drops into the LLM prompt. RiffWriter is
 * a single flat-blob generator with no section-aware output model (see
 * Forgejo #90), so this can't mechanically enforce a scheme per section the
 * way Studio Team's agent pipeline does -- it composes clear, spelled-out
 * instructions the model reads as prose. Runs across multiple providers
 * (Gemini/Claude/OpenAI/xAI), not just Claude with a 236KB spec already in
 * its context, so codes (S3, A1, +PRE) are never sent alone -- always
 * expanded to full instruction text.
 *
 * A value of 'default' (or an empty devices/modifiers array) means "say
 * nothing about this axis" -- matches the original SONG_STRUCTURES/
 * RHYME_SCHEMES behavior for a form nobody has touched.
 */

import type { RhymeConfig, StructureConfig } from '@/types';

const isSet = (value: string): boolean => Boolean(value) && value !== 'default';

/**
 * Builds the RHYME_SCHEME instruction block. Returns '' when every field is
 * still 'default' (nothing to say -- omit the line entirely, same as before).
 */
export function buildRhymeInstructionText(config: RhymeConfig): string {
  const lines: string[] = [];

  if (isSet(config.verseScheme)) lines.push(`Verse rhyme scheme: ${config.verseScheme}`);
  if (isSet(config.chorusScheme)) lines.push(`Chorus rhyme scheme: ${config.chorusScheme}`);
  if (isSet(config.bridgeScheme)) lines.push(`Bridge rhyme scheme: ${config.bridgeScheme}`);
  if (
    isSet(config.verseScheme) &&
    isSet(config.chorusScheme) &&
    config.verseScheme === config.chorusScheme
  ) {
    lines.push('Verse and chorus must not sound identical structurally even though the same scheme was picked for both — vary line length, device, or cadence between them.');
  }

  if (isSet(config.exactness)) lines.push(config.exactness);
  if (isSet(config.cadence)) lines.push(config.cadence);
  if (isSet(config.meter)) lines.push(config.meter);
  for (const device of config.devices.slice(0, 2)) {
    if (isSet(device)) lines.push(device);
  }

  if (lines.length === 0) return '';
  return `RHYME_SCHEME:\n${lines.map(l => `- ${l}`).join('\n')}`;
}

/**
 * Builds the SONG_STRUCTURE instruction block. Returns '' when architecture,
 * modifiers, and ending are all still 'default'/empty.
 */
export function buildStructureInstructionText(config: StructureConfig): string {
  const lines: string[] = [];

  if (isSet(config.architecture)) lines.push(`Architecture: ${config.architecture}`);
  for (const modifier of config.modifiers.slice(0, 2)) {
    if (isSet(modifier)) lines.push(`Include ${modifier}.`);
  }
  if (isSet(config.ending)) lines.push(`Ending: ${config.ending}`);

  if (lines.length === 0) return '';
  return `SONG_STRUCTURE:\n${lines.map(l => `- ${l}`).join('\n')}`;
}
